import { useState, useEffect, useCallback, useRef } from "react";
import { sendError } from "@/lib/client/sentry";
import type { Proxy } from "@/types/generated";
const API_URL = process.env.NEXT_PUBLIC_API_URL || "";

// Tabs left open shouldn't refetch on every glance — only when they've been
// away long enough that the backend's own 5-minute response cache
// (cacheMiddleware.js) could plausibly have rotated.
const MIN_REFETCH_INTERVAL_MS = 60 * 1000;

/**
 * Hook for fetching proxy data grouped by country.
 *
 * Returns both a flat array of all proxies and a grouped array by country.
 * The backend response is cached for 5 minutes (see cacheMiddleware.js) and
 * invalidated immediately on any admin create/update/delete. This hook
 * fetches once on mount and again whenever the tab regains visibility, so a
 * tab left open doesn't keep showing whatever was current when it loaded.
 *
 * @returns Object containing:
 * - proxies: Flat array of all proxy objects
 * - proxiesByCountry: Array of proxies grouped by country
 * - loading: Boolean indicating if data is being fetched
 *
 * @example
 * ```tsx
 * function ProxySelector() {
 *   const { proxiesByCountry, loading } = useProxies();
 *
 *   if (loading) return <Spinner />;
 *
 *   return (
 *     <Select
 *       groups={proxiesByCountry.map(country => ({
 *         label: country.country,
 *         items: country.proxies.map(p => ({
 *           value: p.id,
 *           label: p.name
 *         }))
 *       }))}
 *     />
 *   );
 * }
 * ```
 */
const useProxies = () => {
  const [proxies, setProxies] = useState<Proxy[]>([]);
  const [proxiesByCountry, setProxiesByCountry] = useState<ProxyCountry[]>([]);
  const [loading, setLoading] = useState(false);
  const lastFetchedAtRef = useRef(0);

  const fetchProxies = useCallback(async () => {
    lastFetchedAtRef.current = Date.now();
    setLoading(true);
    try {
      const response = (await fetch(`${API_URL}/api/v2/proxies`, {
        credentials: "include",
      }).then((response) => response.json())) as SuccessResponse<ProxiesResponse>;

      const groupedProxies = response?.data?.response || [];

      // Flatten for backward compatibility
      const flatProxies = groupedProxies.flatMap((country) => country.proxies);

      setProxies(flatProxies);
      setProxiesByCountry(groupedProxies);
    } catch (error) {
      console.warn("Error fetching proxies:", error);
      sendError(error as Error, {});
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchProxies();
  }, [fetchProxies]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastFetchedAtRef.current < MIN_REFETCH_INTERVAL_MS) return;
      fetchProxies();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [fetchProxies]);

  return {
    proxies,
    proxiesByCountry,
    loading,
  };
};

export default useProxies;
